import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import {
  Plus, Search, Loader2, Pencil, Eye, Copy, Archive, ArchiveRestore,
  SlidersHorizontal, Lock, Trash2, GitBranch,
} from 'lucide-react';
import { ModuleBanner } from '@/components/admin/ModuleBanner';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';

const emptyAnchor = (v) => ({ value: v, label_es: '', label_en: '' });

function anchorPreview(scale, max = 3) {
  const a = (scale.anchors || []).slice(0, max);
  const txt = a.map((x) => `${x.value}·${(x.label_i18n || {}).es || ''}`).join('  ');
  return (scale.anchors || []).length > max ? `${txt} …` : txt;
}

function ScalePreview({ scale, t }) {
  if (!scale) return null;
  return (
    <div className="space-y-2" data-testid="scale-preview-anchors">
      {(scale.anchors || []).map((a) => (
        <div key={a.value} className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold tabular-nums">
            {a.value}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{(a.label_i18n || {}).es || '—'}</p>
            {(a.label_i18n || {}).en && (a.label_i18n || {}).en !== (a.label_i18n || {}).es && (
              <p className="text-xs text-muted-foreground truncate">EN: {(a.label_i18n || {}).en}</p>
            )}
          </div>
        </div>
      ))}
      <p className="text-xs text-muted-foreground">
        {t('pages.responseScales.previewHint', { defaultValue: 'This is exactly how the answer options will be labeled on imported likert_5 questions.' })}
      </p>
    </div>
  );
}

export default function AdminResponseScales() {
  const { t } = useTranslation();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';

  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPoints, setFilterPoints] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', reversible: true, anchors: [] });
  const [saving, setSaving] = useState(false);
  const [previewScale, setPreviewScale] = useState(null);

  const reload = () => {
    setLoading(true);
    Promise.all([
      adminApi.listResponseScales(showArchived),
      adminApi.responseScalesUsage().catch(() => []),
    ])
      .then(([scales, use]) => {
        setItems(scales);
        setUsage(Object.fromEntries((use || []).map((u) => [u.scale_id, u])));
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const pointValues = useMemo(
    () => Array.from(new Set(items.map((s) => s.points))).sort((a, b) => a - b),
    [items],
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((sc) => {
      if (filterPoints !== 'all' && String(sc.points) !== filterPoints) return false;
      if (!s) return true;
      return (sc.name || '').toLowerCase().includes(s) || (sc.slug || '').toLowerCase().includes(s);
    });
  }, [items, search, filterPoints]);

  const sort = useSort();
  const sorted = useMemo(() => sort.apply(filtered, {
    name:   (x) => (x.name || '').toLowerCase(),
    points: (x) => x.points || 0,
    type:   (x) => (x.is_system ? 0 : 1),
    usage:  (x) => usage[x.id]?.questionnaire_count || 0,
    status: (x) => (x.active ? 0 : 1),
  }), [filtered, sort, usage]);

  const versionsBySlug = useMemo(() => {
    const m = {};
    for (const s of items) m[s.slug] = (m[s.slug] || 0) + 1;
    return m;
  }, [items]);

  const frozen = (s) => !!usage[s.id]?.published_locked;

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', reversible: true, anchors: [1, 2, 3, 4, 5].map(emptyAnchor) });
    setFormOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '',
      reversible: !!s.reversible,
      anchors: (s.anchors || []).map((a) => ({
        value: a.value, label_es: (a.label_i18n || {}).es || '', label_en: (a.label_i18n || {}).en || '',
      })),
    });
    setFormOpen(true);
  };
  const openDuplicateAsNew = (s) => {
    setEditing(null);
    setForm({
      name: `${s.name} (copy)`,
      reversible: !!s.reversible,
      anchors: (s.anchors || []).map((a) => ({
        value: a.value, label_es: (a.label_i18n || {}).es || '', label_en: (a.label_i18n || {}).en || '',
      })),
    });
    setFormOpen(true);
  };

  const setAnchor = (i, field, v) => {
    setForm((f) => {
      const anchors = f.anchors.map((a, idx) => (idx === i ? { ...a, [field]: v } : a));
      return { ...f, anchors };
    });
  };
  const addAnchor = () => setForm((f) => (
    f.anchors.length >= 11 ? f : { ...f, anchors: [...f.anchors, emptyAnchor(f.anchors.length + 1)] }
  ));
  const removeAnchor = (i) => setForm((f) => (
    f.anchors.length <= 2 ? f : { ...f, anchors: f.anchors.filter((_, idx) => idx !== i).map((a, idx) => ({ ...a, value: idx + 1 })) }
  ));

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('pages.responseScales.nameRequired', { defaultValue: 'Name is required.' })); return; }
    if (form.anchors.some((a) => !a.label_es.trim())) {
      toast.error(t('pages.responseScales.anchorsRequired', { defaultValue: 'Every point needs at least a Spanish label.' }));
      return;
    }
    const payload = {
      name: form.name.trim(),
      reversible: form.reversible,
      anchors: form.anchors.map((a) => ({
        value: a.value, label_es: a.label_es.trim(), label_en: a.label_en.trim() || null,
      })),
    };
    setSaving(true);
    try {
      if (editing && frozen(editing)) {
        await adminApi.newResponseScaleVersion(editing.id, payload);
        toast.success(t('pages.responseScales.newVersionOk', { defaultValue: 'New scale version created — the previous version stays intact for published questionnaires.' }));
      } else if (editing) {
        await adminApi.updateResponseScale(editing.id, payload);
        toast.success(t('pages.responseScales.updatedOk', { defaultValue: 'Scale updated.' }));
      } else {
        await adminApi.createResponseScale(payload);
        toast.success(t('pages.responseScales.createdOk', { defaultValue: 'Scale created — it is immediately available in the import template.' }));
      }
      setFormOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const duplicate = async (s) => {
    try {
      const d = await adminApi.duplicateResponseScale(s.id);
      toast.success(t('pages.responseScales.duplicatedOk', { defaultValue: 'Scale duplicated' }), { description: d.slug });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async (s) => {
    try {
      await adminApi.archiveResponseScale(s.id);
      toast.success(t('pages.responseScales.archivedOk', { defaultValue: 'Scale archived.' }));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reactivate = async (s) => {
    try {
      await adminApi.reactivateResponseScale(s.id);
      toast.success(t('pages.responseScales.reactivatedOk', { defaultValue: 'Scale reactivated.' }));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <Topbar
        title={t('pages.responseScales.pageTitle', { defaultValue: 'Response Scales' })}
        subtitle={t('pages.responseScales.pageSubtitle', { defaultValue: 'Reusable Likert scale library used by the questionnaire import (Template v2)' })}
      />
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-response-scales-page">
        <ModuleBanner id="response-scales" icon={SlidersHorizontal} />

        <Card className="p-4 shadow-card">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="rs-search"
                placeholder={t('pages.responseScales.searchPlaceholder', { defaultValue: 'Search by name or slug…' })}
                value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10"
              />
            </div>
            <Select value={filterPoints} onValueChange={setFilterPoints}>
              <SelectTrigger className="w-full md:w-44 h-10" data-testid="rs-points-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('pages.responseScales.allPoints', { defaultValue: 'All points' })}</SelectItem>
                {pointValues.map((p) => (
                  <SelectItem key={p} value={String(p)}>{t('pages.responseScales.nPoints', { defaultValue: '{{n}} points', n: p })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 px-3 h-10 rounded-md border border-border">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="rs-archived-toggle" />
              <Label className="text-xs">{t('pages.responseScales.showArchived', { defaultValue: 'Show archived' })}</Label>
            </div>
            {isAdmin && (
              <Button data-testid="rs-new-btn" onClick={openNew} className="bg-gradient-primary h-10 gap-1.5">
                <Plus className="h-4 w-4" /> {t('pages.responseScales.newScale', { defaultValue: 'New scale' })}
              </Button>
            )}
          </div>
        </Card>

        <Card className="shadow-card overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-muted-foreground" data-testid="rs-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-12 text-center" data-testid="rs-empty">
              <SlidersHorizontal className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t('pages.responseScales.noMatch', { defaultValue: 'No scales match your search.' })}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortableHeader sort={sort} k="name">{t('pages.responseScales.colName', { defaultValue: 'Scale' })}</SortableHeader></TableHead>
                  <TableHead className="text-center"><SortableHeader sort={sort} k="points">{t('pages.responseScales.colPoints', { defaultValue: 'Points' })}</SortableHeader></TableHead>
                  <TableHead>{t('pages.responseScales.colPreview', { defaultValue: 'Labels' })}</TableHead>
                  <TableHead><SortableHeader sort={sort} k="type">{t('pages.responseScales.colType', { defaultValue: 'Type' })}</SortableHeader></TableHead>
                  <TableHead className="text-center"><SortableHeader sort={sort} k="usage">{t('pages.responseScales.colUsage', { defaultValue: 'Used by' })}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="status">{t('pages.responseScales.colStatus', { defaultValue: 'Status' })}</SortableHeader></TableHead>
                  <TableHead className="text-right">{t('common.actions', { defaultValue: 'Actions' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((s) => {
                  const u = usage[s.id];
                  return (
                    <TableRow key={s.id} data-testid={`rs-row-${s.slug}-v${s.scale_version}`}>
                      <TableCell>
                        <p className="font-medium flex items-center gap-1.5">
                          {s.name}
                          {s.scale_version > 1 && <Badge variant="outline" className="text-xs gap-0.5"><GitBranch className="h-2.5 w-2.5" />v{s.scale_version}</Badge>}
                          {s.superseded_by && <Badge variant="outline" className="text-xs text-muted-foreground">{t('pages.responseScales.superseded', { defaultValue: 'superseded' })}</Badge>}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {s.slug}
                          {versionsBySlug[s.slug] > 1 && ` · ${t('pages.responseScales.nVersions', { defaultValue: '{{n}} versions', n: versionsBySlug[s.slug] })}`}
                        </p>
                      </TableCell>
                      <TableCell className="text-center tabular-nums font-bold">{s.points}</TableCell>
                      <TableCell className="max-w-[280px]">
                        <p className="text-xs text-muted-foreground truncate" title={anchorPreview(s, 11)}>{anchorPreview(s)}</p>
                      </TableCell>
                      <TableCell>
                        {s.is_system
                          ? <Badge variant="outline" className="text-xs gap-1"><Lock className="h-3 w-3" />{t('pages.responseScales.system', { defaultValue: 'System' })}</Badge>
                          : <Badge variant="outline" className="text-xs bg-info/10 text-info border-info/30">{t('pages.responseScales.custom', { defaultValue: 'Custom' })}</Badge>}
                      </TableCell>
                      <TableCell className="text-center">
                        {u?.questionnaire_count ? (
                          <span
                            className="inline-flex items-center gap-1 text-sm font-medium cursor-help"
                            title={(u.questionnaires || []).map((q) => `${q.name} (${q.status})`).join('\n')}
                            data-testid={`rs-usage-${s.slug}`}
                          >
                            {u.questionnaire_count}
                            {u.published_locked && <Lock className="h-3 w-3 text-warning" />}
                          </span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {s.active
                          ? <Badge className="bg-success/10 text-success border-success/20" variant="outline">{t('pages.responseScales.active', { defaultValue: 'active' })}</Badge>
                          : <Badge variant="outline" className="text-muted-foreground">{t('pages.responseScales.archived', { defaultValue: 'archived' })}</Badge>}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setPreviewScale(s)} className="gap-1" data-testid={`rs-preview-${s.slug}`}>
                          <Eye className="h-3.5 w-3.5" /> {t('pages.responseScales.preview', { defaultValue: 'Preview' })}
                        </Button>
                        {isAdmin && !s.is_system && s.active && (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="gap-1" data-testid={`rs-edit-${s.slug}`}>
                            {frozen(s) ? <GitBranch className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                            {frozen(s)
                              ? t('pages.responseScales.newVersion', { defaultValue: 'New version' })
                              : t('common.edit', { defaultValue: 'Edit' })}
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => duplicate(s)} className="gap-1" data-testid={`rs-duplicate-${s.slug}`}>
                            <Copy className="h-3.5 w-3.5" /> {t('pages.responseScales.duplicate', { defaultValue: 'Duplicate' })}
                          </Button>
                        )}
                        {isAdmin && !s.is_system && (
                          s.active ? (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => archive(s)}
                              disabled={frozen(s)}
                              title={frozen(s) ? t('pages.responseScales.lockedHint', { defaultValue: 'Referenced by a published questionnaire — cannot be archived.' }) : undefined}
                              className="gap-1 text-muted-foreground"
                              data-testid={`rs-archive-${s.slug}`}
                            >
                              <Archive className="h-3.5 w-3.5" /> {t('pages.responseScales.archive', { defaultValue: 'Archive' })}
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => reactivate(s)} className="gap-1 text-muted-foreground" data-testid={`rs-reactivate-${s.slug}`}>
                              <ArchiveRestore className="h-3.5 w-3.5" /> {t('pages.responseScales.reactivate', { defaultValue: 'Reactivate' })}
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {/* Preview dialog */}
      <Dialog open={!!previewScale} onOpenChange={(o) => !o && setPreviewScale(null)}>
        <DialogContent className="max-w-lg" data-testid="rs-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewScale?.name}
              <Badge variant="outline" className="text-xs">v{previewScale?.scale_version}</Badge>
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {previewScale?.slug} · {previewScale?.points} {t('pages.responseScales.points', { defaultValue: 'points' })}
              {previewScale?.reversible && ` · ${t('pages.responseScales.reversible', { defaultValue: 'reversible' })}`}
            </DialogDescription>
          </DialogHeader>
          <ScalePreview scale={previewScale} t={t} />
          {usage[previewScale?.id]?.questionnaires?.length > 0 && (
            <div className="rounded-md border border-border p-3">
              <p className="text-xs font-semibold mb-1.5">{t('pages.responseScales.usedIn', { defaultValue: 'Used in' })}</p>
              <ul className="text-xs text-muted-foreground space-y-0.5">
                {usage[previewScale.id].questionnaires.map((q) => (
                  <li key={q.id} className="flex items-center gap-1.5">• {q.name} <Badge variant="outline" className="text-xs">{q.status}</Badge></li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="rs-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? (frozen(editing)
                  ? t('pages.responseScales.newVersionTitle', { defaultValue: 'New version of {{name}}', name: editing.name })
                  : t('pages.responseScales.editTitle', { defaultValue: 'Edit scale' }))
                : t('pages.responseScales.newTitle', { defaultValue: 'New response scale' })}
            </DialogTitle>
            {editing && frozen(editing) && (
              <DialogDescription data-testid="rs-frozen-note">
                {t('pages.responseScales.frozenNote', { defaultValue: 'This scale is referenced by a published questionnaire, so it is frozen. Saving will create version {{v}} — existing questionnaires keep the current labels.', v: (editing.scale_version || 1) + 1 })}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('pages.responseScales.name', { defaultValue: 'Name' })}</Label>
              <Input data-testid="rs-form-name" className="mt-1.5" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {!editing && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('pages.responseScales.slugHint', { defaultValue: 'The slug used in the import template is generated from the name.' })}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.reversible} onCheckedChange={(v) => setForm({ ...form, reversible: v })} data-testid="rs-form-reversible" />
              <Label className="text-sm">{t('pages.responseScales.reversibleLabel', { defaultValue: 'Reversible (supports reverse-scored items)' })}</Label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t('pages.responseScales.anchors', { defaultValue: 'Points & labels' })}</Label>
                <Button size="sm" variant="outline" onClick={addAnchor} disabled={form.anchors.length >= 11} className="gap-1 h-7 text-xs" data-testid="rs-add-anchor">
                  <Plus className="h-3 w-3" /> {t('pages.responseScales.addPoint', { defaultValue: 'Add point' })}
                </Button>
              </div>
              <div className="space-y-2">
                {form.anchors.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-sm font-bold tabular-nums">{a.value}</span>
                    <Input placeholder={t('pages.responseScales.labelEs', { defaultValue: 'Label (ES)' })} value={a.label_es}
                           onChange={(e) => setAnchor(i, 'label_es', e.target.value)} data-testid={`rs-anchor-es-${i}`} />
                    <Input placeholder={t('pages.responseScales.labelEn', { defaultValue: 'Label (EN)' })} value={a.label_en}
                           onChange={(e) => setAnchor(i, 'label_en', e.target.value)} data-testid={`rs-anchor-en-${i}`} />
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground"
                            onClick={() => removeAnchor(i)} disabled={form.anchors.length <= 2} data-testid={`rs-remove-anchor-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFormOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={save} disabled={saving} className="bg-gradient-primary" data-testid="rs-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                editing
                  ? (frozen(editing)
                    ? t('pages.responseScales.saveNewVersion', { defaultValue: 'Save as new version' })
                    : t('common.save', { defaultValue: 'Save' }))
                  : t('pages.responseScales.create', { defaultValue: 'Create scale' })
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
